import React from "react";
import { FileDown, Award, TrendingUp, CheckCircle2, Building2 } from "lucide-react";

const PDF_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_integrated-yards/artifacts/476e293651c13c5c_Queen_City_Metal_Recycling_Executive_Capability_Statement_Final.pdf";

const Metric = ({ value, label }) => (
  <div className="border-t border-black/15 pt-5">
    <div className="font-display text-4xl md:text-5xl leading-none">{value}</div>
    <div className="eyebrow text-black/50 mt-2">{label}</div>
  </div>
);

const CAPABILITIES = [
  "Ferrous & non-ferrous scrap",
  "Light iron / appliances",
  "Heavy steel / structural",
  "Steel turnings / magnetic fines",
  "Copper, brass & insulated wire",
  "Aluminum solids / turnings",
  "Stainless steel / nickel alloys",
  "Electric motors & mill scale",
  "Catalytic converters",
  "Autos, trucks, buses & automotive parts",
  "Heavy / farm equipment & machinery",
  "Forklifts, industrial scrap & oversized materials",
];

const SERVICES = [
  "Transportation",
  "Metal processing",
  "Export support",
  "Demolition-related services",
  "Petroleum-product recycling",
  "Competitive pricing",
];

const PERFORMANCE = [
  {
    title: "Charlotte Douglas Airport",
    body: "Transportation and processing of scrap rebar and cables.",
  },
  {
    title: "BB&T Baseball Stadium",
    body: "Received and processed oversized cast iron valves and pipes.",
  },
  {
    title: "eWaste Processors",
    body: "Acceptance of ferrous and non-ferrous materials from multiple processors.",
  },
  {
    title: "Regional Supply",
    body:
      "Supplies local shredders and regional steel mills with 3,000+ tons of ferrous material monthly.",
  },
];

const ADVANTAGES = [
  "Fast cash payments",
  "NC Certified Public Scale",
  "Ferrous & non-ferrous acceptance",
  "Credit cards accepted",
  "Convenient location",
  "Open 6 days a week",
  "E-scrap accepted",
  "Member: NC Recycling Association",
  "Member: ISRI",
  "Member: Chamber of Commerce",
];

const NAICS = [
  "423930",
  "562111",
  "425110",
  "423140",
  "441120",
  "423510",
  "423110",
  "562920",
  "562219",
  "562998",
];

const Capabilities = () => {
  return (
    <section
      id="capabilities"
      className="section-pad bg-[color:var(--bg-alt)] relative"
      data-testid="capabilities-section"
    >
      <div className="container-x">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="eyebrow text-[color:var(--primary)] mb-4">
              ◆ Capability Statement
            </div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter">
              Built For Volume, <br />
              Complex Streams, <br />
              <span className="text-[color:var(--accent)]">And Partnership.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-black/70 leading-relaxed mb-6">
              A proven metals recycling partner built for responsiveness,
              volume, and complex material streams. We provide transportation,
              processing, export support, demolition-related services, and
              competitive scrap-metal purchasing to industrial, automotive,
              peddler, and commercial customers.
            </p>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noreferrer"
              className="hard-btn-primary"
              data-testid="download-capability-btn"
            >
              <FileDown size={14} /> Download PDF
            </a>
          </div>
        </div>

        {/* Metric strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          <Metric value="2013" label="Established" />
          <Metric value="1,800+" label="Customers / Week" />
          <Metric value="3,000+" label="Tons Ferrous / Month" />
          <Metric value="200+" label="Tons Non-Ferrous / Month" />
        </div>

        {/* 3-column: Materials / Services / Credentials */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          <div
            className="bg-white border border-black/15 p-8"
            data-testid="cap-materials"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border-2 border-black grid place-items-center">
                <Building2 size={16} />
              </div>
              <div>
                <div className="eyebrow text-[color:var(--primary)]">
                  Core Capabilities
                </div>
                <div className="font-display uppercase text-lg leading-none mt-1">
                  Metals & Handling
                </div>
              </div>
            </div>
            <ul className="space-y-0 text-sm">
              {CAPABILITIES.map((c, i) => (
                <li
                  key={c}
                  className="py-2.5 border-t border-black/10 flex items-start gap-3 first:border-t-0"
                >
                  <span className="font-mono text-[10px] text-black/40 pt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-black/15 p-8" data-testid="cap-services">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border-2 border-black grid place-items-center">
                <TrendingUp size={16} />
              </div>
              <div>
                <div className="eyebrow text-[color:var(--primary)]">
                  Service Capabilities
                </div>
                <div className="font-display uppercase text-lg leading-none mt-1">
                  End-to-End
                </div>
              </div>
            </div>
            <ul className="space-y-0 text-sm mb-10">
              {SERVICES.map((s) => (
                <li
                  key={s}
                  className="py-2.5 border-t border-black/10 flex items-center gap-3 first:border-t-0"
                >
                  <CheckCircle2 size={14} className="text-[color:var(--primary)]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>

            <div className="eyebrow text-[color:var(--primary)] mb-3">
              Commercial Advantages
            </div>
            <ul className="space-y-0 text-sm">
              {ADVANTAGES.map((a) => (
                <li
                  key={a}
                  className="py-2.5 border-t border-black/10 flex items-center gap-3 first:border-t-0"
                >
                  <CheckCircle2 size={14} className="text-[color:var(--accent)]" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-black/15 p-8" data-testid="cap-performance">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border-2 border-black grid place-items-center">
                <Award size={16} />
              </div>
              <div>
                <div className="eyebrow text-[color:var(--primary)]">
                  Selected Performance
                </div>
                <div className="font-display uppercase text-lg leading-none mt-1">
                  Track Record
                </div>
              </div>
            </div>
            <ul className="space-y-5 text-sm mb-10">
              {PERFORMANCE.map((p) => (
                <li key={p.title} className="border-t border-black/10 pt-4">
                  <div className="font-display uppercase text-sm mb-1">
                    {p.title}
                  </div>
                  <div className="text-black/70 text-sm leading-relaxed">
                    {p.body}
                  </div>
                </li>
              ))}
            </ul>

            <div className="eyebrow text-[color:var(--primary)] mb-3">
              Credentials
            </div>
            <ul className="space-y-0 text-xs font-mono">
              <li className="py-2 border-t border-black/10">
                <span className="text-black/50">WOSB · </span> Woman Owned Small Business
              </li>
              <li className="py-2 border-t border-black/10">
                <span className="text-black/50">DUNS · </span> 062670524
              </li>
              <li className="py-2 border-t border-black/10">
                <span className="text-black/50">CAGE · </span> 6Z9Z9
              </li>
              <li className="py-2 border-t border-black/10">
                <span className="text-black/50">ISO · </span> ISO-9000 Procedures
              </li>
              <li className="py-2 border-t border-black/10">
                <span className="text-black/50">Member · </span> CLT-Meck Waste Ambassadors
              </li>
            </ul>
          </div>
        </div>

        {/* NAICS */}
        <div
          className="bg-[color:var(--bg-dark)] text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative overflow-hidden"
          data-testid="cap-naics"
        >
          <div className="absolute inset-0 grid-lines-dark opacity-50 pointer-events-none" />
          <div className="relative">
            <div className="eyebrow text-[color:var(--accent)] mb-2">
              NAICS Codes
            </div>
            <div className="font-display text-2xl md:text-3xl uppercase leading-none">
              Procurement Ready
            </div>
          </div>
          <div className="relative flex flex-wrap gap-2">
            {NAICS.map((code) => (
              <span
                key={code}
                className="px-3 py-2 border border-white/30 font-mono text-xs tracking-widest hover:bg-white hover:text-black transition-colors"
              >
                {code}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
